import { Component } from "@theme/component";
import { CartAddEvent } from "@theme/events";
import { morphSection } from "@theme/section-renderer";

class ProductAddToCart extends Component {
  static get observedAttributes() {
    return [
      "data-selected-variant-id",
      "data-price",
      "data-compare-at-price",
      "data-inventory-policy",
      "data-inventory-quantity",
      "data-inventory-management",
      "data-items",
      "data-sold-out",
      "data-enable-safety-rating",
    ];
  }

  constructor() {
    super();
    this.productId = null;
    this.isUs = false;
    this.currencySymbol = null;
    this.currentVariant = null;
    this.bundleItems = null;
    this.button = null;
    this.buttonText = null;
    this.buttonPrice = null;
    this.topCopy = null;
    this.originalTopCopyContent = null;
    this.hurryMessage = null;
    this.isAddingToCart = false;
    this.buttonClickHandler = null;
    this.inlineFormContainer = null;
    this.preOrderConfirmHandler = null;
    this.notifyEmailFormHandler = null;
    this.notifyEmailInlineFormHandler = null;
    this.notifyEmailInlineFormKeyHandler = null;
    this.resizeTimeout = null;
    this.safetyRatingConfirmHandler = null;
    this.enableSafetyRating = false;
    this.bisContainer = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.init();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      this.updateStateFromAttributes();
    }
  }

  init() {
    this.productId = this.dataset.productId ? parseInt(this.dataset.productId, 10) : null;
    this.isUs = this.dataset.isUs === "true";
    this.useKlaviyoWaitlist = this.dataset.useKlaviyoWaitlist === "true";
    this.enableSafetyRating = this.dataset.enableSafetyRating === "true";

    // Get currency symbol
    const currencyScript = this.querySelector("script[data-currency-symbol]");
    this.currencySymbol = currencyScript
      ? JSON.parse(currencyScript.textContent) || currencyScript.dataset.currencySymbol
      : window.Shopify?.currency?.active || "$";

    this.button = this.querySelector("[data-add-to-cart-button]");
    this.buttonText = this.querySelector("[data-button-text]");
    this.buttonPrice = this.querySelector("[data-button-price]");
    this.topCopy = this.querySelector("[data-top-copy]");
    this.hurryMessage = this.querySelector("[data-hurry-message]");

    // Store original top copy content if it exists
    if (this.topCopy) {
      this.originalTopCopyContent = this.topCopy.innerHTML;
    }

    if (this.button) {
      if (this.buttonClickHandler) this.button.removeEventListener("click", this.buttonClickHandler);
      this.buttonClickHandler = this.handleAddToCartClick.bind(this);
      this.button.addEventListener("click", this.buttonClickHandler);
    }

    // Handle window resize for notify email form
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener("resize", this.handleResize);

    // Listen for subscription changes - listen on both component and document
    this.subscriptionChangeHandler = this.handleSubscriptionChange.bind(this);
    this.addEventListener("subscription:changed", this.subscriptionChangeHandler);
    document.addEventListener("subscription:changed", this.subscriptionChangeHandler);

    // Check for existing subscription state from subscription toggle
    this.checkInitialSubscriptionState();

    this.updateStateFromAttributes();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this.handleResize) {
      window.removeEventListener("resize", this.handleResize);
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    if (this.subscriptionChangeHandler) {
      this.removeEventListener("subscription:changed", this.subscriptionChangeHandler);
      document.removeEventListener("subscription:changed", this.subscriptionChangeHandler);
    }
    this.removeNotifyEmailInlineForm();
    this.removeBisContainer();
  }

  checkInitialSubscriptionState() {
    // Check if subscription mode is already set (from subscription toggle initialization)
    if (this.dataset.subscriptionMode) {
      // Trigger refresh to ensure button state is correct
      this.refreshButtonState();
    }
  }

  handleResize() {
    // Debounce resize handling
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout(() => {
      const currentState = this.getCurrentButtonState();
      // Handle resize for bundle products (both US and non-US), non-US single variants, or US with Klaviyo waitlist override
      if (currentState === "out_of_stock" && (this.bundleItems || !this.isUs || this.useKlaviyoWaitlist)) {
        this.handleNotifyEmailState();
      }
    }, 250);
  }

  handleSubscriptionChange(event) {
    // Only handle events for this component
    // If event has targetComponent, verify it's this component
    if (event.detail?.targetComponent && event.detail.targetComponent !== this) {
      return; // Event is for a different component
    }

    // Update data attributes based on subscription selection
    if (event.detail && event.detail.option === "subscribe") {
      if (event.detail.sellingPlanId) {
        this.dataset.sellingPlanId = event.detail.sellingPlanId.toString();
      }
      this.dataset.subscriptionMode = "subscribe";

      // Store subscription price if provided
      if (event.detail.subscriptionPrice) {
        this.dataset.subscriptionPrice = event.detail.subscriptionPrice.toString();
      }
      if (event.detail.subscriptionCompareAtPrice) {
        this.dataset.subscriptionCompareAtPrice = event.detail.subscriptionCompareAtPrice.toString();
      }
    } else {
      this.removeAttribute("data-selling-plan-id");
      this.removeAttribute("data-subscription-price");
      this.removeAttribute("data-subscription-compare-at-price");
      this.dataset.subscriptionMode = "one-time";
    }

    // Ensure variant data is loaded before refreshing
    if (!this.currentVariant && !this.bundleItems) {
      this.updateStateFromAttributes();
    }

    // Refresh button state to reflect subscription change
    this.refreshButtonState();
  }

  refreshButtonState() {
    // Re-evaluate button state based on current variant and subscription mode
    // If variant data isn't loaded yet, try to reload it from attributes
    if (!this.currentVariant && !this.bundleItems) {
      this.updateStateFromAttributes();
      return;
    }

    if (this.bundleItems) {
      this.updateBundleState();
    } else if (this.currentVariant) {
      this.updateSingleState();
    } else {
      // Fallback: try to update from attributes
      this.updateStateFromAttributes();
    }
  }

  updateStateFromAttributes() {
    // Update enableSafetyRating from dataset when attributes change
    this.enableSafetyRating = this.dataset.enableSafetyRating === "true";

    // 1. Check for Bundle Mode
    if (this.dataset.items) {
      try {
        this.bundleItems = JSON.parse(this.dataset.items);
        this.currentVariant = null;
        this.updateBundleState();
        return;
      } catch (e) {
        console.warn("[ProductAddToCart] Invalid data-items JSON", e);
      }
    }

    // 2. Fallback to Single Variant Mode
    const variantId = this.dataset.selectedVariantId;
    if (variantId) {
      this.bundleItems = null;
      const hasSoldOutTag = this.dataset.soldOut === "true";
      this.currentVariant = {
        id: parseInt(variantId, 10),
        price: parseInt(this.dataset.price, 10),
        compare_at_price: this.dataset.compareAtPrice ? parseInt(this.dataset.compareAtPrice, 10) : null,
        inventory_policy: this.dataset.inventoryPolicy,
        inventory_quantity: parseInt(this.dataset.inventoryQuantity, 10),
        inventory_management: this.dataset.inventoryManagement || null,
        available: true,
        requires_selling_plan: false, // Set to false for now as per requirements
        has_sold_out_tag: hasSoldOutTag,
      };

      // Unavailable: inventory_quantity <= 0 && inventory_policy === "deny"
      if (this.currentVariant.inventory_quantity <= 0 && this.currentVariant.inventory_policy === "deny") {
        this.currentVariant.available = false;
      }

      this.updateSingleState();
    }
  }

  updateBundleState() {
    if (!this.bundleItems || this.bundleItems.length === 0) {
      delete this.dataset.bundleDisplayPriceCents;
      this.setButtonState("sold_out");
      if (this.buttonPrice) {
        this.buttonPrice.innerHTML = "";
      }
      // Hide hurry message for empty bundle
      this.updateHurryMessage(null);
      return;
    }

    // Get bundle state based on new requirements
    const state = this.getBundleButtonState();
    this.setButtonState(state);

    if (["default", "pre_order", "subscribe"].includes(state)) {
      let totalRegularPrice = 0;
      this.bundleItems.forEach(item => {
        if (item.price) totalRegularPrice += item.price;
      });

      let discountPercentage = parseInt(this.dataset.bundleDiscountPercent || "0", 10);
      if (!Number.isFinite(discountPercentage) || discountPercentage <= 0) {
        const bundleAtcComponent = document.querySelector("bundle-atc-component");
        discountPercentage = bundleAtcComponent
          ? parseInt(bundleAtcComponent.dataset.discountPercentage || "0", 10)
          : 0;
      }
      discountPercentage = Math.min(100, Math.max(0, discountPercentage));

      const discountMultiplier = (100 - discountPercentage) / 100;
      const totalDiscountedPrice = Math.floor(totalRegularPrice * discountMultiplier);

      this.dataset.bundleDisplayPriceCents = String(totalDiscountedPrice);

      if (this.buttonPrice) {
        const aggregateVariant = {
          price: totalDiscountedPrice,
          compare_at_price: discountPercentage > 0 ? totalRegularPrice : null,
        };
        this.buttonPrice.innerHTML = ` - ${this.formatPriceDisplay(aggregateVariant)}`;
      }
    } else {
      delete this.dataset.bundleDisplayPriceCents;
      if (this.buttonPrice) {
        this.buttonPrice.innerHTML = "";
      }
    }

    // For bundles, check if any item matches hurry conditions
    // Use the first item that matches, or null if none match
    const hurryVariant = this.bundleItems.find(item => {
      return item.inventory_policy === "deny" && item.inventory_quantity > 0 && item.inventory_quantity < 100;
    });
    this.updateHurryMessage(hurryVariant || null);
  }

  updateSingleState() {
    const variant = this.currentVariant;
    const state = this.getButtonState(variant);
    this.setButtonState(state);

    if (this.buttonPrice) {
      if (["default", "pre_order", "subscribe"].includes(state) && variant) {
        // If subscribe state, use subscription price if available
        if (state === "subscribe" && this.dataset.subscriptionPrice) {
          const subscriptionPrice = parseInt(this.dataset.subscriptionPrice, 10);
          const subscriptionCompareAtPrice = this.dataset.subscriptionCompareAtPrice
            ? parseInt(this.dataset.subscriptionCompareAtPrice, 10)
            : null;

          const subscriptionVariant = {
            price: subscriptionPrice,
            compare_at_price: subscriptionCompareAtPrice,
          };
          this.buttonPrice.innerHTML = ` - ${this.formatPriceDisplay(subscriptionVariant)}`;
        } else {
          // Use regular variant price
          this.buttonPrice.innerHTML = ` - ${this.formatPriceDisplay(variant)}`;
        }
      } else {
        // Clear price for out_of_stock, sold_out, and other states
        this.buttonPrice.innerHTML = "";
      }
    }

    // Keep loyalty strip in sync using the same effective price as the button above.
    if (variant) {
      const effectivePrice =
        state === "subscribe" && this.dataset.subscriptionPrice
          ? parseInt(this.dataset.subscriptionPrice, 10)
          : variant.price;
      this._updateLoyaltyStrip(effectivePrice);
    }

    // Update hurry message visibility
    this.updateHurryMessage(variant);
  }

  /**
   * Sync the non-bundle loyalty earn strip inside this component.
   * Called from updateSingleState so it fires on the same tick as the button price,
   * including after a style-selector morph (attributeChangedCallback → data-price change).
   * @param {number} priceCents
   */
  _updateLoyaltyStrip(priceCents) {
    // Only handle single-mode ATC. Real bundle strips are managed by loyalty-points-earn.js.
    if (this.hasAttribute("data-items")) return;

    const strip = this.querySelector('[data-loyalty-earn-root][data-loyalty-context="pdp"]');
    if (!(strip instanceof HTMLElement)) return;

    // Strip may have a stale data-loyalty-earn-bundle from a previous product that was in
    // bundle mode (the subtree is preserved across style-selector morphs via
    // data-skip-subtree-update). Remove it so loyalty-points-earn.js also treats this as a
    // regular strip on subsequent variant:changed / syncPdpLoyaltyFromVariantPicker calls.
    if (strip.hasAttribute("data-loyalty-earn-bundle")) {
      strip.removeAttribute("data-loyalty-earn-bundle");
    }

    // Heal stale data-product-id: after a style-selector morph the ATC subtree is
    // preserved (data-skip-subtree-update) but the ATC element's own data-product-id
    // IS updated by morph's copyAttributes. Keep the strip in sync.
    const pid = this.dataset.productId;
    if (pid && pid !== strip.dataset.productId) {
      strip.dataset.productId = pid;
    }

    const cents = Number(priceCents) || 0;
    strip.dataset.variantPriceCents = String(cents);

    const cfg = /** @type {any} */ (window).Theme?.loyaltyEarn;
    if (!(cfg?.enabledAddToCart ?? cfg?.enabled)) return;

    const ppl = Math.max(1, Math.min(20, Number(cfg.pointsPerDollar) || 1));
    const mult = Number(cfg.promoMultiplier) > 0 ? Number(cfg.promoMultiplier) : 1;
    const units = Math.floor(cents / 100);
    const pts = Math.round(units * ppl * mult);

    strip.hidden = false;
    strip.querySelectorAll("[data-loyalty-earn-value]").forEach(el => {
      el.textContent = String(pts);
    });
  }

  setButtonState(state) {
    if (!this.button || !this.buttonText) return;

    // Handle out_of_stock state for bundle products: show email flow for both US and non-US
    if (state === "out_of_stock" && this.bundleItems) {
      this.handleNotifyEmailState();
      return;
    }

    // Handle out_of_stock state for non-US users (single variant)
    if (state === "out_of_stock" && !this.isUs) {
      this.handleNotifyEmailState();
      return;
    }

    // Postscript BIS (US single-variant out_of_stock): reinstate when `sdk.postscript.io` is uncommented in layout/theme.liquid.
    // if (state === "out_of_stock" && this.isUs) {
    //   this.handleBisContainerState();
    //   return;
    // }

    // Interim: Postscript SDK disabled — sold-out CTA instead of hidden ATC + empty #ps__bis_target_container.
    // When useKlaviyoWaitlist is enabled, route US out-of-stock to the Klaviyo email flow instead.
    if (state === "out_of_stock" && this.isUs && !this.bundleItems) {
      if (this.useKlaviyoWaitlist) {
        this.handleNotifyEmailState();
        return;
      }
      this.removeBisContainer();
      this.removeNotifyEmailInlineForm();
      if (this.button) {
        this.button.style.display = "";
        this.button.disabled = true;
      }
      if (this.buttonText) {
        this.buttonText.textContent = this.getButtonText("sold_out");
      }
      if (this.button) {
        this.button.className = this.button.className.replace(/product__addToCartButton--\S+/g, "");
        this.button.classList.add("product__addToCartButton--sold-out");
      }
      this.updateTopCopy("sold_out");
      return;
    }

    // Remove notify email inline form if it exists
    this.removeNotifyEmailInlineForm();

    // Remove BIS container if it exists
    this.removeBisContainer();

    this.button.disabled = state === "sold_out";
    this.buttonText.textContent = this.getButtonText(state);

    this.button.className = this.button.className.replace(/product__addToCartButton--\S+/g, "");
    this.button.classList.add(`product__addToCartButton--${state.replace(/_/g, "-")}`);

    // Update top copy for out_of_stock state
    this.updateTopCopy(state);
  }

  handleNotifyEmailState() {
    // Always show button by default (both desktop and mobile)
    // Form will be shown when button is clicked
    this.removeNotifyEmailInlineForm();
    this.button.disabled = false;
    this.buttonText.textContent = this.getButtonText("out_of_stock");
    this.button.className = this.button.className.replace(/product__addToCartButton--\S+/g, "");
    this.button.classList.add("product__addToCartButton--out-of-stock");
    this.updateTopCopy("out_of_stock");
  }

  showNotifyEmailInlineForm() {
    // Remove existing inline form if any
    this.removeNotifyEmailInlineForm();

    // Hide the button
    if (this.button) {
      this.button.style.display = "none";
    }

    // Create inline form container
    this.inlineFormContainer = document.createElement("div");
    this.inlineFormContainer.className = "notify-email-inline-form";
    this.inlineFormContainer.innerHTML = `
      <div class="flex gap-2">
        <input
          type="email"
          name="email"
          class="notify-email-inline-input"
          placeholder="Email Address"
          autocomplete="email"
          required
          data-notify-email-inline-input
          aria-label="Email address"
        />
        <button
        type="button"
        class="notify-email-inline-submit btn btn-lg btn-primary-asphalt"
        data-notify-email-inline-submit
        aria-label="Submit email notification"
        >
        <span data-notify-email-inline-submit-text>Submit</span>
      </button>
      </div>
      <div class="notify-email-inline-error hidden m-2" data-notify-email-inline-error role="alert">
        <span data-notify-email-inline-error-text></span>
      </div>
      <div class="notify-email-inline-message hidden" data-notify-email-inline-message role="alert">
        <span data-notify-email-inline-message-text></span>
      </div>
    `;

    // Insert after button or at the end of the component
    if (this.button && this.button.parentNode) {
      this.button.parentNode.insertBefore(this.inlineFormContainer, this.button.nextSibling);
    } else {
      this.appendChild(this.inlineFormContainer);
    }

    // Set up form handler
    this.setupNotifyEmailInlineForm();
  }

  removeNotifyEmailInlineForm() {
    if (this.inlineFormContainer && this.inlineFormContainer.parentNode) {
      this.inlineFormContainer.parentNode.removeChild(this.inlineFormContainer);
      this.inlineFormContainer = null;
    }
    if (this.button) {
      this.button.style.display = "";
    }
  }

  handleBisContainerState() {
    // Only show BIS container for single variant mode (not bundle mode)
    if (this.bundleItems) {
      // For bundle mode, show regular button state
      this.removeBisContainer();
      this.removeNotifyEmailInlineForm();
      this.button.disabled = false;
      this.buttonText.textContent = this.getButtonText("out_of_stock");
      this.button.className = this.button.className.replace(/product__addToCartButton--\S+/g, "");
      this.button.classList.add("product__addToCartButton--out-of-stock");
      this.updateTopCopy("out_of_stock");
      return;
    }

    // Remove notify email inline form if it exists
    this.removeNotifyEmailInlineForm();

    // Show BIS container and hide button
    this.showBisContainer();
  }

  showBisContainer() {
    // Remove existing BIS container if any
    this.removeBisContainer();

    // Hide the button
    if (this.button) {
      this.button.style.display = "none";
    }

    // Get current variant ID (for single variant mode only)
    const variantId = this.currentVariant?.id || this.dataset.selectedVariantId;

    if (!variantId) {
      console.warn("[ProductAddToCart] No variant ID available for BIS container");
      return;
    }

    // Create BIS container
    this.bisContainer = document.createElement("div");
    this.bisContainer.innerHTML = `
      <div id="ps__bis_target_container"></div>
      <div id="current-variant" style="display: none">${variantId}</div>
    `;

    // Insert after button or at the end of the component
    if (this.button && this.button.parentNode) {
      this.button.parentNode.insertBefore(this.bisContainer, this.button.nextSibling);
    } else {
      this.appendChild(this.bisContainer);
    }
  }

  removeBisContainer() {
    if (this.bisContainer && this.bisContainer.parentNode) {
      this.bisContainer.parentNode.removeChild(this.bisContainer);
      this.bisContainer = null;
    }
    if (this.button) {
      this.button.style.display = "";
    }
  }

  setupNotifyEmailInlineForm() {
    if (!this.inlineFormContainer) return;

    const input = this.inlineFormContainer.querySelector("[data-notify-email-inline-input]");
    const submitBtn = this.inlineFormContainer.querySelector("[data-notify-email-inline-submit]");
    const errorDiv = this.inlineFormContainer.querySelector("[data-notify-email-inline-error]");
    const messageDiv = this.inlineFormContainer.querySelector("[data-notify-email-inline-message]");

    if (!input || !submitBtn) return;

    // Remove existing handler
    if (this.notifyEmailInlineFormHandler) {
      submitBtn.removeEventListener("click", this.notifyEmailInlineFormHandler);
      input.removeEventListener("keypress", this.notifyEmailInlineFormKeyHandler);
    }

    // Create handlers
    this.notifyEmailInlineFormHandler = event => {
      event.preventDefault();
      this.handleNotifyEmailSubmit(input, submitBtn, errorDiv, messageDiv, true);
    };

    this.notifyEmailInlineFormKeyHandler = event => {
      if (event.key === "Enter") {
        event.preventDefault();
        this.handleNotifyEmailSubmit(input, submitBtn, errorDiv, messageDiv, true);
      }
    };

    submitBtn.addEventListener("click", this.notifyEmailInlineFormHandler);
    input.addEventListener("keypress", this.notifyEmailInlineFormKeyHandler);
  }

  updateTopCopy(state) {
    if (!this.topCopy) return;

    const topCopyElement = this.topCopy;
    if (state === "out_of_stock") {
      topCopyElement.textContent = "Out of stock!";
      if (topCopyElement instanceof HTMLElement) {
        topCopyElement.style.color = "var(--color-cherry)";
      }
    } else {
      // Restore original content or clear if it was empty
      if (this.originalTopCopyContent) {
        topCopyElement.innerHTML = this.originalTopCopyContent;
        if (topCopyElement instanceof HTMLElement) {
          topCopyElement.style.color = "";
        }
      } else {
        topCopyElement.textContent = "";
        if (topCopyElement instanceof HTMLElement) {
          topCopyElement.style.color = "";
        }
      }
    }
  }

  updateHurryMessage(variant) {
    if (!this.hurryMessage) return;

    // Show message if variant matches conditions:
    // - inventory_policy === "deny"
    // - inventory_quantity > 0 and < 100
    const shouldShow =
      variant &&
      variant.inventory_policy === "deny" &&
      variant.inventory_quantity > 0 &&
      variant.inventory_quantity < 100;

    if (shouldShow) {
      this.hurryMessage.removeAttribute("hidden");
    } else {
      this.hurryMessage.setAttribute("hidden", "");
    }

    // Update button text to reflect urgency label when hurry message is visible
    if (this.buttonText && this.button) {
      const currentState = this.getCurrentButtonState();
      if (currentState === "default") {
        const newText = this.getButtonText(currentState);
        this.buttonText.textContent = newText;
        this.button.setAttribute("aria-label", newText);
      }
    }
  }

  getButtonText(state) {
    const settings = {
      default: this.dataset.buttonTextDefault ?? "Add to Cart",
      preOrder: this.dataset.buttonTextPreOrder ?? "Pre-Order",
      subscribe: this.dataset.buttonTextSubscribe ?? "Subscribe",
      outOfStockUs: this.dataset.buttonTextOutOfStockUs ?? "Notify me via text",
      outOfStockInternational: this.dataset.buttonTextOutOfStockInternational ?? "Notify me via email",
      soldOut: this.dataset.buttonTextSoldOut ?? "Sold Out",
      urgencyLabel: this.dataset.buttonTextUrgencyLabel ?? "get it before it's gone",
    };

    // Check if hurry message is visible - if so, use urgency label for default state
    if (state === "default" && this.hurryMessage && !this.hurryMessage.hasAttribute("hidden")) {
      return settings.urgencyLabel;
    }

    // For bundle products or when Klaviyo waitlist override is active, always show "Notify me via email" for out_of_stock
    let outOfStockText;
    if (state === "out_of_stock" && this.bundleItems) {
      outOfStockText = settings.outOfStockInternational;
    } else if (state === "out_of_stock" && this.useKlaviyoWaitlist && this.isUs) {
      outOfStockText = settings.outOfStockInternational;
    } else {
      outOfStockText = this.isUs ? settings.outOfStockUs : settings.outOfStockInternational;
    }

    const stateMap = {
      pre_order: settings.preOrder,
      subscribe: settings.subscribe,
      out_of_stock: outOfStockText,
      sold_out: settings.soldOut,
    };

    return stateMap[state] ?? settings.default;
  }

  formatPrice(priceInCents) {
    if (isNaN(priceInCents) || priceInCents === null) return { floor: "", sup: "" };
    const floor = Math.floor(priceInCents / 100);
    const sup = priceInCents % 100;
    return {
      floor: `${this.currencySymbol}${floor}`,
      sup: String(sup).padStart(2, "0"),
    };
  }

  formatPriceDisplay(variant) {
    if (!variant || variant.price === null) return "";

    const priceFormatted = this.formatPrice(variant.price);
    const priceSup = parseInt(priceFormatted.sup, 10);
    const productId = this.productId ?? "";

    const priceHtml = `<span class="igPrice" data-product-id="${productId}">${priceFormatted.floor}${priceSup > 0 ? `<sup class="top-[-0.2em]">${priceFormatted.sup}</sup>` : ""}</span>`;

    const { compare_at_price: compareAtPrice, price } = variant;
    if (compareAtPrice && compareAtPrice > price && compareAtPrice > 0) {
      const comparePriceFormatted = this.formatPrice(compareAtPrice);
      const comparePriceSup = parseInt(comparePriceFormatted.sup, 10);
      const comparePriceHtml = `<span class="text-(--color-gray-accessible) text-12-regular line-through decoration-white compareAtPrice"><span class="igComparePrice text-white" data-product-id="${productId}">${comparePriceFormatted.floor}${comparePriceSup > 0 ? `<sup class="top-[-0.3em]">${comparePriceFormatted.sup}</sup>` : ""}</span></span>`;
      return `${priceHtml} ${comparePriceHtml}`;
    }

    return priceHtml;
  }

  getBundleButtonState() {
    if (!this.bundleItems || this.bundleItems.length === 0) {
      return "sold_out";
    }

    // Priority order: Pre order → Subscribe → Add to cart → Notify Me → Sold out

    // 1. Check for Pre order: Any variant's inventory_policy === "continue"
    const hasPreOrder = this.bundleItems.some(item => item.inventory_policy === "continue");
    if (hasPreOrder) return "pre_order";

    // 2. Check for Subscribe: subscription mode is active (from subscription toggle)
    // This takes priority over variant.requires_selling_plan to allow manual subscription selection
    const subscriptionMode = this.dataset.subscriptionMode;
    if (subscriptionMode === "subscribe") {
      // Return subscribe state if subscription mode is active
      return "subscribe";
    }

    // 2b. Fallback: Check if any variant has requires_selling_plan === true
    const hasSubscribe = this.bundleItems.some(item => item.requires_selling_plan === true);
    if (hasSubscribe) return "subscribe";

    // 3. If all items have inventory_management as nil (not managed), show default state
    const allUnmanaged = this.bundleItems.every(item => {
      return !item.inventory_management || item.inventory_management === "" || item.inventory_management !== "shopify";
    });
    if (allUnmanaged) return "default";

    // 4. Check for Add to cart: All variants available with inventory > 0 and policy = "deny"
    const allAvailable = this.bundleItems.every(item => {
      return item.available && item.inventory_quantity > 0 && item.inventory_policy === "deny";
    });
    if (allAvailable) return "default";

    // 5. Check for Notify Me: Any variant unavailable, policy = "deny"
    const hasUnavailableDeny = this.bundleItems.some(item => {
      const isUnavailable = !item.available || (item.inventory_quantity <= 0 && item.inventory_policy === "deny");
      return isUnavailable && item.inventory_policy === "deny";
    });
    if (hasUnavailableDeny) {
      // Check if any variant has sold_out tag
      const hasSoldOutTag = this.bundleItems.some(item => item.has_sold_out_tag === true);
      if (hasSoldOutTag) {
        return "sold_out";
      }
      return "out_of_stock";
    }

    // 6. Default to sold_out
    return "sold_out";
  }

  getButtonState(variant) {
    if (!variant) return "sold_out";

    // Priority order: Pre order → Subscribe → Add to cart → Notify Me → Sold out

    // 1. Pre order: inventory_policy === "continue" (regardless of quantity)
    if (variant.inventory_policy === "continue") {
      return "pre_order";
    }

    // 2. Subscribe: check if subscription mode is active (from subscription toggle)
    // This takes priority over variant.requires_selling_plan to allow manual subscription selection
    const subscriptionMode = this.dataset.subscriptionMode;
    if (subscriptionMode === "subscribe") {
      // Return subscribe state if subscription mode is active, even if sellingPlanId isn't set yet
      // The sellingPlanId will be set by the subscription toggle event
      return "subscribe";
    }

    // 2b. Fallback: variant requires selling plan (for products that always require subscription)
    if (variant.requires_selling_plan === true) {
      return "subscribe";
    }

    // 3. If inventory_management is nil (not managed), show default state
    if (
      !variant.inventory_management ||
      variant.inventory_management === "" ||
      variant.inventory_management !== "shopify"
    ) {
      return "default";
    }

    // 4. Add to cart: available, inventory > 0, and inventory_policy === "deny"
    if (variant.available && variant.inventory_quantity > 0 && variant.inventory_policy === "deny") {
      return "default";
    }

    // 5. Notify Me / Sold out: unavailable (inventory_quantity <= 0 && inventory_policy === "deny")
    const isUnavailable =
      !variant.available || (variant.inventory_quantity <= 0 && variant.inventory_policy === "deny");
    if (isUnavailable && variant.inventory_policy === "deny") {
      // Check if product has sold_out tag
      if (variant.has_sold_out_tag === true) {
        return "sold_out";
      }
      return "out_of_stock";
    }

    // 6. Default to sold_out
    return "sold_out";
  }

  async handleAddToCartClick(event) {
    event.preventDefault();
    if (this.isAddingToCart) return;

    // Check if current state is pre_order - show modal instead
    const currentState = this.getCurrentButtonState();
    if (currentState === "pre_order") {
      this.showPreOrderModal();
      return;
    }

    // Check if current state is out_of_stock: show email flow for bundles (both US and non-US), non-US single variants, or US with Klaviyo waitlist override
    if (currentState === "out_of_stock" && (this.bundleItems || !this.isUs || this.useKlaviyoWaitlist)) {
      if (window.innerWidth < 768) {
        // Mobile: show modal
        this.showNotifyEmailModal();
      } else {
        // Desktop: show inline form
        this.showNotifyEmailInlineForm();
      }
      return;
    }

    // Check if product has "Enable Safety Rating" tag and user is outside US
    // Show modal for default or subscribe states
    if (this.enableSafetyRating && !this.isUs && (currentState === "default" || currentState === "subscribe")) {
      this.showSafetyRatingModal();
      return;
    }

    await this.addToCart();
  }

  getCurrentButtonState() {
    if (this.bundleItems) {
      return this.getBundleButtonState();
    } else if (this.currentVariant) {
      return this.getButtonState(this.currentVariant);
    }
    return "sold_out";
  }

  showPreOrderModal() {
    const modal = document.querySelector("#pre-order-modal");
    if (!modal) {
      console.warn("[ProductAddToCart] Pre-order modal not found");
      return;
    }

    // Set the confirm button text to match the default add to cart button text
    const confirmButton = modal.querySelector("[data-pre-order-confirm]");
    const confirmButtonText = modal.querySelector("[data-pre-order-confirm-text]");
    if (confirmButton && confirmButtonText) {
      const defaultText = this.dataset.buttonTextDefault ?? "Add to Cart";
      confirmButtonText.textContent = defaultText;
    }

    // Show the modal
    if (typeof modal.showDialog === "function") {
      modal.showDialog();
    } else {
      // Fallback: try to find the dialog and show it
      const dialog = modal.querySelector("dialog");
      if (dialog && typeof dialog.showModal === "function") {
        dialog.showModal();
      }
    }

    // Set up the confirm button handler
    this.setupPreOrderModalConfirm(modal);
  }

  setupPreOrderModalConfirm(modal) {
    const confirmButton = modal.querySelector("[data-pre-order-confirm]");
    if (!confirmButton) return;

    // Remove any existing handler
    if (this.preOrderConfirmHandler) {
      confirmButton.removeEventListener("click", this.preOrderConfirmHandler);
    }

    // Create new handler
    this.preOrderConfirmHandler = async event => {
      event.preventDefault();
      event.stopPropagation();

      // Close the modal first
      if (typeof modal.closeDialog === "function") {
        await modal.closeDialog();
      } else {
        // Fallback: try to find the dialog and close it
        const dialog = modal.querySelector("dialog");
        if (dialog && typeof dialog.close === "function") {
          dialog.close();
        }
      }

      // Then add to cart
      await this.addToCart();
    };

    confirmButton.addEventListener("click", this.preOrderConfirmHandler);

    // Clean up handler when modal closes
    const cleanup = () => {
      if (this.preOrderConfirmHandler) {
        confirmButton.removeEventListener("click", this.preOrderConfirmHandler);
        modal.removeEventListener("dialog:close", cleanup);
      }
    };
    modal.addEventListener("dialog:close", cleanup, { once: true });
  }

  showNotifyEmailModal() {
    const modal = document.querySelector("#notify-me-email-modal");
    if (!modal) {
      console.warn("[ProductAddToCart] Notify me email modal not found");
      return;
    }

    // Show the modal
    if (typeof modal.showDialog === "function") {
      modal.showDialog();
    } else {
      const dialog = modal.querySelector("dialog");
      if (dialog && typeof dialog.showModal === "function") {
        dialog.showModal();
      }
    }

    // Set up the form handler
    this.setupNotifyEmailModalForm(modal);
  }

  setupNotifyEmailModalForm(modal) {
    const form = modal.querySelector("[data-notify-email-form]");
    const input = modal.querySelector("[data-notify-email-input]");
    const submitBtn = modal.querySelector("[data-notify-email-submit]");
    const errorDiv = modal.querySelector("[data-notify-email-error]");
    const messageDiv = modal.querySelector("[data-notify-email-message]");

    if (!form || !input || !submitBtn) return;

    // Remove existing handler
    if (this.notifyEmailFormHandler) {
      form.removeEventListener("submit", this.notifyEmailFormHandler);
    }

    // Create new handler
    this.notifyEmailFormHandler = event => {
      event.preventDefault();
      this.handleNotifyEmailSubmit(input, submitBtn, errorDiv, messageDiv, false);
    };

    form.addEventListener("submit", this.notifyEmailFormHandler);
  }

  async handleNotifyEmailSubmit(input, submitBtn, errorDiv, messageDiv, isInline) {
    const email = input.value.trim();

    // Hide previous messages
    if (errorDiv) {
      errorDiv.classList.add("hidden");
    }
    if (messageDiv) {
      messageDiv.classList.add("hidden");
    }

    // Validate email
    if (!email) {
      this.showNotifyEmailError("Please enter an email address.", errorDiv, messageDiv);
      return;
    }

    if (!this.validateEmail(email)) {
      this.showNotifyEmailError("Please enter a valid email address.", errorDiv, messageDiv);
      return;
    }

    // Show loading state
    const submitText =
      submitBtn.querySelector("[data-notify-email-submit-text]") ||
      submitBtn.querySelector("[data-notify-email-inline-submit-text]");
    const originalText = submitText ? submitText.textContent : submitBtn.textContent;
    if (submitText) {
      submitText.textContent = "Submitting...";
    } else {
      submitBtn.textContent = "Submitting...";
    }
    submitBtn.disabled = true;

    // Get Klaviyo ID and Form ID
    const klaviyoId = this.dataset.klaviyoId;
    const formId = this.dataset.klaviyoFormId;

    if (!klaviyoId) {
      this.showNotifyEmailError("Klaviyo ID is not configured.", errorDiv, messageDiv);
      if (submitText) {
        submitText.textContent = originalText;
      } else {
        submitBtn.textContent = originalText;
      }
      submitBtn.disabled = false;
      return;
    }

    try {
      // Get variant ID: for bundles, get the out-of-stock variant ID
      let variantId = null;
      if (this.bundleItems) {
        // Find the first out-of-stock variant in the bundle
        const outOfStockVariant = this.bundleItems.find(item => {
          const isUnavailable = !item.available || (item.inventory_quantity <= 0 && item.inventory_policy === "deny");
          return isUnavailable && item.inventory_policy === "deny";
        });
        variantId = outOfStockVariant?.id || null;
      } else {
        // Single variant mode
        variantId = this.currentVariant?.id || this.dataset.selectedVariantId;
      }

      if (!variantId) {
        this.showNotifyEmailError("Variant information is not available.", errorDiv, messageDiv);
        if (submitText) {
          submitText.textContent = originalText;
        } else {
          submitBtn.textContent = originalText;
        }
        submitBtn.disabled = false;
        return;
      }

      // Build request URL
      const url = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(klaviyoId)}`;

      // Prepare JSON payload following Klaviyo API structure
      const payload = {
        data: {
          type: "back-in-stock-subscription",
          attributes: {
            profile: {
              data: {
                type: "profile",
                attributes: {
                  email: email,
                },
              },
            },
            channels: ["EMAIL"],
          },
          relationships: {
            variant: {
              data: {
                type: "catalog-variant",
                id: `$shopify:::$default:::${variantId}`,
              },
            },
          },
        },
      };

      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/vnd.api+json",
          revision: "2025-10-15",
        },
        body: JSON.stringify(payload),
      };

      const response = await fetch(url, requestOptions);

      // Use response.text() as per Vue implementation
      const responseText = await response.text();

      if (response.ok) {
        // Success
        const successMessage = "Thanks for signing up.";
        this.showNotifyEmailSuccess(successMessage, errorDiv, messageDiv);
        input.value = "";

        // Close modal if not inline
        if (!isInline) {
          const modal = document.querySelector("#notify-me-email-modal");
          if (modal && typeof modal.closeDialog === "function") {
            setTimeout(() => {
              modal.closeDialog();
            }, 2000);
          }
        }
      } else {
        // Error
        this.showNotifyEmailError("An error occurred. Please try again", errorDiv, messageDiv);
      }
    } catch (error) {
      console.error("[ProductAddToCart] Notify email error:", error);
      this.showNotifyEmailError("An error occurred. Please try again", errorDiv, messageDiv);
    } finally {
      if (submitText) {
        submitText.textContent = originalText;
      } else {
        submitBtn.textContent = originalText;
      }
      submitBtn.disabled = false;
    }
  }

  showNotifyEmailError(message, errorDiv, messageDiv) {
    if (errorDiv) {
      const errorText =
        errorDiv.querySelector("[data-notify-email-error-text]") ||
        errorDiv.querySelector("[data-notify-email-inline-error-text]");
      if (errorText) {
        errorText.textContent = message;
      }
      errorDiv.classList.remove("hidden");
    }
    if (messageDiv) {
      messageDiv.classList.add("hidden");
    }
  }

  showNotifyEmailSuccess(message, errorDiv, messageDiv) {
    if (errorDiv) {
      errorDiv.classList.add("hidden");
    }
    if (messageDiv) {
      const messageText = messageDiv.querySelector("[data-notify-email-message-text]");
      if (messageText) {
        messageText.textContent = message;
      } else {
        messageDiv.textContent = message;
      }
      messageDiv.classList.remove("hidden");
      messageDiv.classList.remove("error");
      messageDiv.classList.add("success");
    }
  }

  validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  showSafetyRatingModal() {
    const modal = document.querySelector("#safety-rating-modal");
    if (!modal) {
      console.warn("[ProductAddToCart] Safety rating modal not found");
      return;
    }

    // Show the modal
    if (typeof modal.showDialog === "function") {
      modal.showDialog();
    } else {
      // Fallback: try to find the dialog and show it
      const dialog = modal.querySelector("dialog");
      if (dialog && typeof dialog.showModal === "function") {
        dialog.showModal();
      }
    }

    // Set up the confirm button handler
    this.setupSafetyRatingModalConfirm(modal);
  }

  setupSafetyRatingModalConfirm(modal) {
    const confirmButton = modal.querySelector("[data-safety-rating-confirm]");
    if (!confirmButton) return;

    // Remove any existing handler
    if (this.safetyRatingConfirmHandler) {
      confirmButton.removeEventListener("click", this.safetyRatingConfirmHandler);
    }

    // Create new handler
    this.safetyRatingConfirmHandler = async event => {
      event.preventDefault();
      event.stopPropagation();

      // Close the modal first
      if (typeof modal.closeDialog === "function") {
        await modal.closeDialog();
      } else {
        // Fallback: try to find the dialog and close it
        const dialog = modal.querySelector("dialog");
        if (dialog && typeof dialog.close === "function") {
          dialog.close();
        }
      }

      // Then add to cart with safety rating property
      await this.addToCart(true);
    };

    confirmButton.addEventListener("click", this.safetyRatingConfirmHandler);

    // Clean up handler when modal closes
    const cleanup = () => {
      if (this.safetyRatingConfirmHandler) {
        confirmButton.removeEventListener("click", this.safetyRatingConfirmHandler);
        modal.removeEventListener("dialog:close", cleanup);
      }
    };
    modal.addEventListener("dialog:close", cleanup, { once: true });
  }

  async addToCart(includeSafetyRating = false) {
    if (this.isAddingToCart) return;

    if (!this.bundleItems && !this.currentVariant) {
      console.warn("[ProductAddToCart] No items to add.");
      return;
    }

    this.isAddingToCart = true;
    if (this.button) {
      this.button.disabled = true;
      if (this.buttonText) {
        this.buttonText.textContent = "Adding...";
      }
    }

    try {
      let payload = {};
      let source = "product";

      if (this.bundleItems) {
        source = "bundle";

        // Get bundle properties from parent bundle-atc-component if it exists
        let bundleProperties = {};
        const bundleAtcComponent = document.querySelector("bundle-atc-component");
        if (bundleAtcComponent && typeof bundleAtcComponent.collectBundleProperties === "function") {
          bundleProperties = bundleAtcComponent.collectBundleProperties();
        }

        // Add safety rating property if needed
        if (includeSafetyRating) {
          bundleProperties._enable_safety_rating = true;
        }

        payload = {
          items: this.bundleItems.map(item => ({
            id: item.id,
            quantity: 1,
            properties: {
              ...(item.properties || {}),
              ...bundleProperties,
            },
            selling_plan: item.selling_plan || null,
          })),
        };
      } else {
        let properties = {};
        try {
          if (this.dataset.cartProperties) properties = JSON.parse(this.dataset.cartProperties);
        } catch (e) {
          console.warn("Error parsing properties", e);
        }

        // Add safety rating property if needed
        if (includeSafetyRating) {
          properties._enable_safety_rating = true;
        }

        // Get selling plan ID if subscription is selected
        const sellingPlanId = this.dataset.sellingPlanId ? parseInt(this.dataset.sellingPlanId, 10) : null;

        payload = {
          items: [
            {
              id: this.currentVariant.id,
              quantity: 1,
              properties: properties,
              selling_plan: sellingPlanId || null,
            },
          ],
        };
      }

      await this.addToCartAndOpenDrawer(payload, source);
    } catch (error) {
      console.error("[ProductAddToCart] Error:", error);
      alert(error?.message ?? "Failed to add to cart.");
    } finally {
      this.isAddingToCart = false;
      if (this.button) {
        this.button.disabled = false;
      }

      if (this.bundleItems) {
        this.setButtonState("default");
      } else {
        const state = this.getButtonState(this.currentVariant);
        this.setButtonState(state);
      }
    }
  }

  async addToCartAndOpenDrawer(payload, source = "product") {
    document.dispatchEvent(new CustomEvent("cart:add-start"));

    // Pre-calculate GWP eligibility and bundle into the same request.
    // Uses shared utility (gwp-cart-utils.js) — single source of truth.
    try {
      const utils = /** @type {any} */ (window).GwpCartUtils;
      if (utils?.getItemsToBundle && payload.items) {
        let addingPrice = 0;
        for (const item of payload.items) {
          let unitPrice = 0;
          if (this.currentVariant && item.id === this.currentVariant.id) {
            unitPrice = this.currentVariant.price || 0;
          } else if (this.bundleItems) {
            const match = this.bundleItems.find((/** @type {any} */ b) => b.id === item.id);
            if (match) unitPrice = match.price || 0;
          }
          addingPrice += unitPrice * (item.quantity || 1);
        }
        const gwpItems = utils.getItemsToBundle(addingPrice);
        for (const gi of gwpItems) {
          if (!payload.items.some(/** @param {any} pi */ pi => pi.id === gi.id)) {
            payload.items.push(gi);
          }
        }
      }
    } catch (_) {
      // Non-critical — GWP tier manager handles it as fallback
    }

    // Collect section IDs so we can bundle them into the POST
    const cartItemsComponents = document.querySelectorAll("cart-items-component");
    const sectionsToUpdate = new Set();
    cartItemsComponents.forEach(item => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // Shopify returns section HTML in the same response, eliminating a separate GET
    if (sectionsToUpdate.size > 0) {
      payload.sections = Array.from(sectionsToUpdate).join(",");
      payload.sections_url = window.location.pathname;
    }

    const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || "Failed to add items to cart");
    }

    const cartData = await response.json();

    const sections = cartData.sections || {};

    for (const cartSectionId of sectionsToUpdate) {
      if (sections[cartSectionId]) {
        morphSection(cartSectionId, sections[cartSectionId]);
      }
    }

    const sectionId = this.dataset.sectionId || "product-section";
    const cartAddEvent = new CartAddEvent(cartData, sectionId, {
      source: source,
      itemCount: cartData.item_count,
      sections: sections,
    });

    document.dispatchEvent(cartAddEvent);

    return cartData;
  }
}

if (!customElements.get("product-add-to-cart")) {
  customElements.define("product-add-to-cart", ProductAddToCart);
}
